/*
modifing AcumaticaEntity fields through @Acumatica decorator

export class DACEntity extends AcumaticaEntity {
  // import nested data to explicit AcumaticaEntity type
  @Acumatica(ChildDetailType) Details!: ChildDetailType[];
  
  // use different field names between Acumatica API and local object
  @Acumatica("AcumaticaAPIFieldName") AlternativeInternalFieldName: type;
  
  // exclude field from being uploaded to Acumatica
  @Acumatica({ write: false }) Field: type;

  // exclude field from being read from Acumatica
  @Acumatica({ read: false }) Field: type;

  // pre-defined field converters between Acumatica type and internal type ( Dates & Booleans )
  @Acumatica({ format: AcumaticaFieldFormat.Date }) Field: Date;
  @Acumatica({ format: AcumaticaFieldFormat.Boolean }) Field: boolean;

  // custom reader / writer functions -- modify value when reading from / writing to Acumatica
  @Acumatica({
      writer: (internal_field_value: any, entity: AcumaticaEntity): any => {
          let TypedEntity = <ExtendedAcumaticaEntity>entity;
          return value_to_write_to_Acumatica
      },
      reader: (acumatica_api_field_value: any, object: any, entity: AcumaticaEntity): any => {
          let TypedEntity = <ExtendedAcumaticaEntity>entity;
          return value_to_use_internally
      },

  })

  // custom 'Usr' Acumatica fields (not necessary to explicitly add to Web Service Endpoint)
  @Acumatica({ custom: {view: "ViewName", field: "UsrFieldName"}}) FieldName: type;
}
*/

import { AcumaticaService } from "./acumatica.service";
import { AcumaticaEntity, AcumaticaEntityService } from "./EntityServices/acumatica-entity";

const AcumaticaFieldMetadataKey = Symbol('AcumaticaFields');

export enum AcumaticaFieldFormat {
    Boolean,
    Date,
    StringList,
}

export interface AcumaticaCustomField {
    view: string;
    field: string;
}

export interface AcumaticaStringListItem {
    internal: string,
    external: string
}

/** represents a StringList map for use in @Acumatica decorators handling conversion between external/display and internal values */
export class AcumaticaStringList implements Iterable<AcumaticaStringListItem> {
    /*
    class DerivedEntity extends AcumaticaEntity {
        @Acumatica(new AcumaticaStringList([
            ['A', 'Alpha'],
            ['B', 'Bravo'],
            ...
        ])) PropertyField;
    }
    */

    private _values: AcumaticaStringListItem[];

    constructor(values: [string, string][]) {
        this._values = values.map(item => ({internal:item[0], external:item[1]}));
    }

    // prevent modification of internal values array object but expose the values such as for rendering UI options
    public get values(): [string, string][] { return [...this.values]; }

    // create and return an Iterator object that is instantiated and used in an *ngFor operation
    [Symbol.iterator](): Iterator<AcumaticaStringListItem> {
        let index = 0;
        let values = this._values;
        return {
            next: (): IteratorResult<AcumaticaStringListItem> => {
                if(index < values.length)
                    return {value: values[index++], done: false };
                else
                    return {value:undefined, done: true};
            }
        }
    }

    /** returns the internal key value for the given external/display value */
    public getInternal(externalValue: string): string | undefined {
        let match = this._values.find(value => value.external == externalValue);
        if(match === undefined) {
            console.error("Unable to map external value \"" + externalValue + "\" in StringList: " + this._values.toString());
            return undefined;            
        }
        return match.internal;
    }

    /** returns the external/display value for the given internal key value */
    public getExternal(internalValue: string): string | undefined {
        let match = this._values.find(value => value.internal == internalValue);
        if(match === undefined) {
            console.error("Unable to map internal value \"" + internalValue + "\" in StringList: " + this._values.toString());
            return undefined;            
        }
        return match.external;
    }
}

export interface AcumaticaFieldRules {
    type?: ({ new(): object }) | ({ new(acumatica: AcumaticaService): object });
    name?: string;
    format?: AcumaticaFieldFormat;
    read?: boolean;
    write?: boolean | ((value: any, entity: any) => boolean);
    stringList?: AcumaticaStringList;

    /**
     * handler function invoked when a value is read / decoded from Acumatica. Returning a value other than undefined will replace the value in the local property
     * @param value the value being read from Acumatica
     * @param entity the local AcumaticaEntity object reference being decoded into
     * @param object the plain JSON object from Acumatica
     * @returns an alternative value to store to the local object
     */
    reader?: (value: any, entity: AcumaticaEntity, object: any) => any;

    /**
     * handler function invoked when a value is being sent to Acumatica. Returning a value other than undefined will replace the value sent to Acumatica
     * @param value the value of the property being encoded for Acumatica
     * @param entity the AcumaticaEntity object reference being sent to Acumatica
     * @returns an alternative value to write to Acumatica
     */
    writer?: (value: any, entity: any) => any;

    custom?: AcumaticaCustomField;
}

export function Acumatica(): Function;
export function Acumatica(rename: string): Function;
export function Acumatica(rules: AcumaticaFieldRules): Function;
export function Acumatica(stringList: AcumaticaStringList): Function;
export function Acumatica(ctor: { new(): object }): Function;
// export function Acumatica(ctor: { new(acumatica: AcumaticaService): object }): Function;
export function Acumatica(arg?:
    AcumaticaStringList |
    AcumaticaFieldRules |
    string |
    ({ new(): object })
    // ({ new(acumatica: AcumaticaService): object })
): Function {
    return (target: AcumaticaEntity, property: string) => {
        // let isEntityClass = !(target instanceof AcumaticaEntity);

        let rules: AcumaticaFieldRules = {};

        if (arg === undefined) {
            // field will simply be added to importFields list
        } else if (typeof arg === 'function') {
            // constructor
            rules.type = arg;
        } else if (typeof arg === 'string') {
            // field renaming
            rules.name = arg;
        } else if (typeof arg === 'object') {
            if(arg instanceof AcumaticaStringList) {
                rules.format = AcumaticaFieldFormat.StringList;
                rules.stringList = arg;
            }
            // AcumaticaFieldRules object
            else rules = arg;
        } else {
            throw new Error('Unexpected value passed to AcumaticaField initializer')
        }

        // create a container object on the entity prototype for modification rules of this property
        if (!Reflect.has(target, AcumaticaFieldMetadataKey)) {
            Reflect.set(target, AcumaticaFieldMetadataKey, {
                // object class constructor
                ctor: {},
                // array of string field names to read from & write to Acumatica
                fields: [],
                // object mapping Acumatica entity field name to internal field name
                rename_external: {},
                // object mapping internal field name to Acumatica entity field name
                rename_internal: {},
                // object mapping Acumatica entity field to function to modify value for internal use
                reader: {},
                // object mapping internal field to function to modify value before sending to Acumatica
                writer: {},
                // object mapping internal fields to Acumatica custom 'Usr' fields to read
                custom: {
                    // custom['UsrLifeCycle'] = { view: "ItemSettings", field: "UsrLifeCycle" }
                    internal: {},
                    // custom.external.ItemSettings.UsrLifeCycle = "LifeCycle"
                    external: {}
                }
            });
        }
        let fieldRules = Reflect.get(target, AcumaticaFieldMetadataKey);

        // initialize the fieldRules object for this property
        fieldRules.fields.push(property);
        if (rules.type) fieldRules.ctor[property] = rules.type;
        if (rules.name) fieldRules.rename_internal[property] = rules.name;
        if (rules.name) fieldRules.rename_external[rules.name] = property;
        if (typeof (rules.read) === 'function') fieldRules.reader[property] = rules.read;
        if (rules.read === false) fieldRules.reader[property] = () => { return undefined; }
        if (typeof (rules.write) === 'function') fieldRules.writer[property] = rules.write;
        if (rules.write === false) fieldRules.writer[property] = () => { return undefined; }
        switch (rules.format) {
            case undefined: break;
            case AcumaticaFieldFormat.Boolean:
                rules.reader = (value: any) => typeof value == 'boolean' ? (value || false) : (!!parseInt(value));
                break;
            case AcumaticaFieldFormat.Date:
                rules.reader = (value: string) => new Date(value);
                rules.writer = (value: Date) => new Date(value).toISO8601();
                break;
            case AcumaticaFieldFormat.StringList:
                // Acumatica API returns the user/display/external value when Getting data
                rules.reader = (value: string) => rules.stringList!.getInternal(value);
                // but expects the internal key value when Updating data -- no rules.writer function necessary
                break;
            default:
                throw new Error("AcumaticaFieldType reader/writer not implemented");
        }
        if (rules.reader !== undefined) fieldRules.reader[property] = rules.reader;
        if (rules.writer !== undefined) fieldRules.writer[property] = rules.writer;
        if (rules.custom !== undefined) {
            fieldRules.custom.internal[property] = rules.custom;
            fieldRules.custom.external[rules.custom.view] = {};
            fieldRules.custom.external[rules.custom.view][rules.custom.field] = property;
        }

    }
}

export function GetAcumaticaFields(target: AcumaticaEntity | AcumaticaEntityService): any {
    // given an AcumaticaEntityService
    if (target instanceof AcumaticaEntityService) {
        target = (<AcumaticaEntityService>target).entityType;
    }

    // given a class (constructor function)
    //      GetAcumaticaFields( InventoryItem );
    let proto = (<any>target)?.prototype;

    // given an object instance
    //      let target = new InventoryItem();
    //      GetAcumaticaFields( target );
    if (proto === undefined) proto = (<any>target).__proto__;

    return Reflect.get(proto, AcumaticaFieldMetadataKey) || {};
}